import {
  Box,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import React, {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { SouthRounded, NorthRounded, UnfoldMoreRounded } from "@mui/icons-material";
import { useAppContext } from "context";
import { API } from "../../api/api";
import { convertToUtc, getTimeZone, toRangeEndUtc, toRangeStartUtc, ToFormData } from "../../hooks/Method";
import DeepSearch from "./DeepSearch";
import Export from "./Export";
import { DateRange, InputCheck, LoadError, SkeletonBlock } from "controls";
import useLatestRequest from "../../hooks/useLatestRequest";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

//  Helper: remove empty / null filters before API call
const cleanParams = (obj) =>
  Object.fromEntries(
    Object.entries(obj).filter(
      ([_, v]) => v !== "" && v !== null && v !== undefined,
    ),
  );

function DynamicTable({
  id = "",
  enableExport = false,
  enableDateRange = false,
  showDefaultDate = false,
  defaultDate,
  headers = [],
  setRecords,
  records = [],
  HeaderComp = <div></div>,
  ActionComp = <div></div>,
  dynamicSearch = true,
  FooterRow,
  endpointKey,
  exportEndpoint,
  pdfExportEndpoint,
  exportFormats = ["excel"],
  fetchData,
  onExport,
  enableSelect = false,
  setSelected,
  selected,
  setResult,
  disableHeader = false,
  // Tightens the gap between the toolbar and the table. Opt-in and defaulted off, so
  // the ~64 existing list screens keep their current rhythm untouched; screens whose
  // toolbar carries short controls (stat chips rather than tall filters) can close the
  // gap without every other screen shifting.
  compactHeader = false,
  // Silent refetch: no bar, no fade. For a table that polls (Desktop App, every 20s)
  // or sits in a row of tabs, where a loader on each refresh is noise. The first-load
  // skeleton still shows.
  disableLoader = false,
  onDateRangeChange,

  // Opt-in column sorting. A header with `sortable: true` becomes clickable and
  // shows a ↑/↓ arrow; clicking calls onHeaderSort(columnId). The PARENT owns
  // the sortBy/sortDir state (and passes them into otherParams to drive the
  // backend). Headers without `sortable` are unaffected.
  sortBy,
  sortDir,
  onHeaderSort,

  // Optional custom control(s) rendered in the right-hand toolbar cluster
  // (before Export / Date / Search) — e.g. a prominent sort-order toggle.
  // Node or () => node. Undefined renders nothing.
  toolbarExtra,

  // Opt-in search scopes, e.g. [{ value: "all", label }, { value: "code", label }].
  // Draws a segmented switch against the search box and sends the chosen value as
  // `searchMode`; the first entry is the default. Screens without it send nothing new.
  searchModes,

  otherParams = {
    outletId: null,
    saleType: null,
    jvStatus: null,
    jvType: null,
    isVariant: null,
  },
}) {
  // Maximum rows selectable at once. The server honours whatever we send (each
  // list repo does .Take(param.MaxSelect)), so this constant is the only knob.
  //
  // Bounded by the form-post shape, not by the query: bulk screens send one field
  // per property per row, and the price editor sends 17 of them. 500 x 17 = 8500
  // fields, which is why Program.cs raises FormOptions.ValueCountLimit to 16384 —
  // the framework default of 1024 would 400 the request with no useful message.
  // Raise this and that limit together, or bulk price edits break first.
  const maxSelect = enableSelect ? 500 : 0;
  const { authUser, selCompany } = useAppContext();
  const { t } = useTranslation();

  const [localLoading, setLocalLoading] = useState(true);
  // First load draws skeleton rows; after that a refetch fades the rows it is replacing.
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const recordsReq = useLatestRequest();
  const firstLoad = localLoading && !hasLoaded;
  const refetching = localLoading && hasLoaded;
  const [pageNum, setPageNum] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchVal, setSearchVal] = useState("");
  const [searchMode, setSearchMode] = useState(searchModes?.[0]?.value ?? null);
  // A boolean, not the array: callers pass a fresh array literal every render.
  const hasSearchModes = !!searchModes?.length;
  const [totalRecord, setTotalRecord] = useState(null);
  const [maxSelectItems, setMaxSelectItems] = useState([]);

  const [sortDate, setSortDate] = useState({
    fromDate: showDefaultDate
      ? defaultDate?.fromDate || dayjs().startOf("month")
      : "",
    toDate: showDefaultDate
      ? defaultDate?.toDate || dayjs().endOf("month")
      : "",
  });

  useEffect(() => {
    onDateRangeChange && onDateRangeChange(sortDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortDate.fromDate, sortDate.toDate]);

  // Every filter in otherParams refetches, compared by value. This used to be a
  // hand-kept list of keys, and a filter missing from it silently never refetched:
  // Cash Safe Ledger kept the first safe's id, and the screen looked filtered when it
  // was not. By value, so callers passing a fresh object literal each render (most of
  // them) don't refetch on every render either.
  const otherParamsKey = JSON.stringify(cleanParams(otherParams));
  const normalizedOtherParams = useMemo(
    () => cleanParams(otherParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [otherParamsKey],
  );

  const param = useMemo(
    () =>
      cleanParams({
        id,
        pageNum,
        rowsPerPage,
        searchVal: searchVal || null,
        searchMode: hasSearchModes ? searchMode : null,
        // Snap to the day boundaries of the dates the user picked, so the end day
        // is INCLUDED. convertToUtc alone gave midnight-at-the-start of the end
        // day, which excluded that whole day from `date <= toDate` filters.
        fromDate: sortDate.fromDate ? toRangeStartUtc(sortDate.fromDate) : null,
        toDate: sortDate.toDate ? toRangeEndUtc(sortDate.toDate) : null,
        today: convertToUtc(new Date()),
        timeZone: getTimeZone(),
        companyId: selCompany || null,
        maxSelect,
        ...normalizedOtherParams,
      }),
    [
      id,
      pageNum,
      rowsPerPage,
      searchVal,
      hasSearchModes,
      searchMode,
      sortDate.fromDate,
      sortDate.toDate,
      selCompany,
      maxSelect,
      normalizedOtherParams,
    ],
  );

  // const fetchRecords = () => {
  //   if (!disableLoader) {
  //     setLoader(true);
  //     setLocalLoading(true);
  //   }

  //   API(endpointKey).post(
  //     ToFormData({
  //       ...param,
  //       fromDate: convertToUtc(param.fromDate),
  //       toDate: convertToUtc(param.toDate),
  //       today: convertToUtc(param.today),
  //       maxSelect,
  //       companyId: selCompany || "",
  //     }),
  //     (res) => {
  //       if (res && res.status === 200) {
  //         setRecords([...res.result]);
  //         setTotalRecord(res.totalRecord || 0);
  //         setMaxSelectItems(res.maxSelectItems || []);
  //         setResult && setResult({ ...res });
  //       } else setRecords([]);

  //       setLoader(false);
  //       setLocalLoading(false);
  //     },
  //   );
  // };

  const fetchRecords = async () => {
    const myReq = recordsReq.begin();
    // The table's own skeleton / bar is the ONE loader for its data. The app-wide top
    // bar used to run as well, so every list showed two loaders for one fetch.
    if (!disableLoader) setLocalLoading(true);

    const handleResult = (res) => {
      // Two quick filter changes: the first reply can land last. Only the newest
      // may fill the table; the newer request clears the loaders when it lands.
      if (!recordsReq.isLatest(myReq)) return;
      if (res?.status === 200) {
        setRecords(res.result || []);
        setTotalRecord(res.totalRecord || 0);
        setMaxSelectItems(res.maxSelectItems || []);
        setResult?.(res);
        setLoadFailed(false);
        setHasLoaded(true);
      } else {
        // Keep the last good rows under the red line. Clearing them showed
        // "Data not available", which reads as an empty list, not a failure.
        setLoadFailed(true);
      }
      setLocalLoading(false);
    };

    if (fetchData) {
      try {
        const res = await fetchData(param);
        handleResult(res);
      } catch {
        handleResult(null);
      }
    } else {
      API(endpointKey).post(ToFormData(param), handleResult);
    }
  };

  // Effect: main reload triggers
  useEffect(() => {
    if (authUser) fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    authUser,
    pageNum,
    rowsPerPage,
    sortDate.fromDate,
    sortDate.toDate,
    endpointKey,
    id,
    selCompany,
    normalizedOtherParams,
  ]);

  // useEffect(() => {
  //   authUser && fetchRecords();
  //   return () => {};
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [authUser, pageNum, rowsPerPage, sortDate, endpointKey, id, selCompany]);

  // useEffect(() => {
  //   if (searchVal.length <= 0 && authUser) {
  //     setPageNum(0);
  //     fetchRecords();
  //   }

  //   return () => {};
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [searchVal]);

  useEffect(() => {
    if (searchVal.length === 0 && authUser) {
      setPageNum(0);
      fetchRecords();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchVal]);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPageNum(0);
  };

  const handleChangePage = (event, newPage) => {
    setPageNum(newPage);
  };

  // A new search starts on the first page. Searching from page 3 used to ask for
  // page 3 of the matches, which is usually empty and reads as "nothing found".
  // When the page does change, the reload effect does the fetch.
  const searchFromFirstPage = () => {
    if (pageNum !== 0) setPageNum(0);
    else fetchRecords();
  };

  const handleSearch = (e) => {
    e.preventDefault();
    searchFromFirstPage();
  };

  // Switching scope only changes anything when there is a term to apply it to;
  // with an empty box it would refetch the same unfiltered page.
  const modeMounted = useRef(false);
  useEffect(() => {
    if (!modeMounted.current) {
      modeMounted.current = true;
      return;
    }
    if (searchVal.trim() && authUser) searchFromFirstPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchMode]);

  const handleRecSelect = (e, item) => {
    if (selected.items.length >= maxSelect && e.target.checked) {
      toast.error(t("maxSelectWarning", { max: maxSelect }));
      return;
    } else if (e.target.checked) {
      setSelected((prev) => ({
        ...prev,
        items: [...prev.items, item.id],
      }));
    } else
      setSelected((prev) => ({
        ...prev,
        items: prev.items.filter((i) => i !== item.id),
      }));
  };

  const handleSelectAll = (e) => {
    var checked = e.target.checked;
    if (checked) {
      setSelected((prev) => ({
        ...prev,
        items: [...maxSelectItems],
        all: checked,
      }));
      if (totalRecord > maxSelect) {
        toast.warning(t("maxSelectInfo", { max: maxSelect }));
      }
    } else setSelected((prev) => ({ ...prev, all: checked, items: [] }));
  };

  return (
    <>
      {!disableHeader && (
        <Box
          // Radius comes from --toolbar-radius, sized against the buttons and inputs
          // this bar holds so it reads as their frame rather than a separate, rounder
          // shape. It was a hardcoded 18px, which also ignored the modern template's
          // flatter tokens.
          className={`flex justify-between items-center flex-wrap gap-2 bg-white rounded-[var(--toolbar-radius)] ${
            compactHeader ? "mb-[6px] p-[4px]" : "mb-[15px] p-[5px]"
          }`}
        >
          <Box className="flex flex-1 min-w-0 basis-full md:basis-auto">
            {/* Invoke as a function when passed as one, so a new arrow-function
                identity on every parent render does NOT cause a remount (which
                would wipe inner filter state). */}
            {React.isValidElement(HeaderComp)
              ? HeaderComp
              : typeof HeaderComp === "function"
              ? HeaderComp()
              : null}
          </Box>
          {dynamicSearch && (
            <div className="flex flex-wrap gap-2 w-full md:w-auto items-center">
              {toolbarExtra &&
                (typeof toolbarExtra === "function" ? toolbarExtra() : toolbarExtra)}
              {enableExport && (
                <Export
                  endpoint={exportEndpoint}
                  pdfEndpoint={pdfExportEndpoint}
                  formats={exportFormats}
                  value={param}
                  onExport={onExport}
                />
              )}
              {enableDateRange && <DateRange {...{ sortDate, setSortDate }} />}
              <DeepSearch
                {...{
                  searchVal,
                  setSearchVal,
                  handleSearch,
                  searchModes,
                  searchMode,
                  setSearchMode,
                }}
              />
            </div>
          )}
        </Box>
      )}

      {/* Table  */}
      <Paper
        sx={{
          width: "100%",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* Thin bar across the top of the table while a filter, page or search loads -
            over the header, so it is where the eye already is and takes no height. */}
        {localLoading && (
          <LinearProgress
            aria-label={t("reportUpdating")}
            sx={{ position: "absolute", top: 0, left: 0, right: 0, height: "3px", zIndex: 4 }}
          />
        )}
        {loadFailed && !localLoading && <LoadError onRetry={fetchRecords} />}
        <TableContainer sx={{ maxHeight: "calc(100vh - 184px)" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {enableSelect && (
                  <TableCell>
                    <InputCheck
                      checked={selected.all}
                      onChange={handleSelectAll}
                      sx={{
                        "& .MuiButtonBase-root": { padding: "0px", ml: "10px" },
                      }}
                    />
                  </TableCell>
                )}

                {headers.map((item, i) => {
                  const canSort = item.sortable && typeof onHeaderSort === "function";
                  const isActive = canSort && sortBy === item.id;
                  return (
                    <TableCell
                      key={i}
                      onClick={canSort ? () => onHeaderSort(item.id) : undefined}
                      sx={{
                        fontSize: "14px",
                        backgroundColor: "var(--white-color)",
                        color: "var(--primary-color)",
                        borderBottom: "1px solid var(--primaryMD-color)",
                        minWidth: "80px",
                        textTransform: "uppercase",
                        whiteSpace: "nowrap",
                        padding: "16px 8px 16px 16px",
                        ...(canSort && {
                          cursor: "pointer",
                          userSelect: "none",
                          "&:hover": { backgroundColor: "var(--primaryXS-color)" },
                        }),
                      }}
                    >
                      {canSort ? (
                        <span className="inline-flex items-center gap-1">
                          {item.label}
                          {isActive ? (
                            sortDir === "asc" ? (
                              <NorthRounded sx={{ fontSize: 16 }} />
                            ) : (
                              <SouthRounded sx={{ fontSize: 16 }} />
                            )
                          ) : (
                            <UnfoldMoreRounded sx={{ fontSize: 16, opacity: 0.35 }} />
                          )}
                        </span>
                      ) : (
                        item.label
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody
              aria-busy={refetching || undefined}
              sx={{
                transition: "opacity 150ms",
                ...(refetching && { opacity: 0.5, pointerEvents: "none", userSelect: "none" }),
              }}
            >
              {firstLoad &&
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    {Array.from({ length: headers.length + (enableSelect ? 1 : 0) }).map((__, j) => (
                      <TableCell key={j} sx={{ padding: "12px 16px" }}>
                        <SkeletonBlock className={`h-4 rounded ${(i + j) % 3 === 0 ? "w-2/3" : "w-full"}`} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              {!firstLoad &&
                records &&
                records.map((item, i) => {
                  return (
                    <TableRow hover key={i}>
                      {/* ------- Checkbox for Select -------  */}
                      {enableSelect && (
                        <TableCell>
                          <InputCheck
                            checked={selected.items?.some((s) => s === item.id)}
                            name={`${item.id}${item.label}`}
                            onChange={(e) => handleRecSelect(e, item)}
                            sx={{
                              "& .MuiButtonBase-root": {
                                padding: "0px",
                                ml: "10px",
                              },
                            }}
                          />
                        </TableCell>
                      )}
                      {headers.map((headItem, index) => {
                        return (
                          <Fragment key={index}>
                            {/* ------- Table Data -------  */}
                            <TableCell
                              key={index}
                              sx={{
                                fontSize: "16px",
                                padding: "6px 16px 6px 16px",
                                whiteSpace: "nowrap",
                                verticalAlign: "top",
                                color: "var(--gray-color)",
                              }}
                            >
                              {headItem.id === "actions" ? (
                                <ActionComp {...item} />
                              ) : (
                                item[headItem.id]
                              )}
                            </TableCell>
                          </Fragment>
                        );
                      })}
                    </TableRow>
                  );
                })}

              {FooterRow && !firstLoad && <TableRow>{<FooterRow />}</TableRow>}
            </TableBody>
          </Table>

          {!totalRecord && !localLoading && !loadFailed && (
            <Typography
              variant="h6"
              className="tx-center p-30 tx-upp gray-color"
            >
              {t("dataNotAvailable")}
            </Typography>
          )}
        </TableContainer>
        {/* Stays on screen while a page loads (it used to vanish and jump the layout);
            paused so a second click can't queue behind the first. */}
        {totalRecord > 0 && (
          <TablePagination
            sx={localLoading ? { pointerEvents: "none", opacity: 0.6 } : undefined}
            rowsPerPageOptions={[10, 20, 50, 100]}
            component="div"
            labelRowsPerPage={t("row")}
            count={totalRecord}
            rowsPerPage={rowsPerPage}
            page={pageNum}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>
    </>
  );
}

export default DynamicTable;
